import React, { useEffect, useState, useRef } from 'react';
import {
  Table, Modal, OverlayTrigger, Tooltip,
  Button, Pagination, FormControl, InputGroup
} from 'react-bootstrap';
import ReportingTypeForm from './ReportingTypesForm';
import { useFirestore } from '../contexts/FirestoreContext';
import {
  ChevronDown, ChevronUp, ArrowDownUp,
  Trash, Wrench, PlusCircle, PlusCircleFill, Search
} from 'react-bootstrap-icons';

function ReportingTypeList({ search }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [showDelete, setShowDelete] = useState(false);
  const [delItem, setDelItem] = useState(null);
  const [searchQuery, setSearchQuery] = useState(search);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [filtered, setFiltered] = useState([]);
  const [isHovered, setIsHovered] = useState(false);
  const cleanupRef = useRef(null);

  const { deleteData, onSnapshotData } = useFirestore();

  // Real-time subscription to reportingTypes
  useEffect(() => {
    const unsubscribe = onSnapshotData('reportingTypes', data => {
      setItems(data);
    });

    return () => {
      // Firestore onSnapshot returns an unsubscribe function
      unsubscribe();
    };
  }, [onSnapshotData]);

  // Filter whenever items or searchQuery change
  useEffect(() => {
    const q = searchQuery.toLowerCase();
    const f = items.filter(i =>
      i.factory.toLowerCase().includes(q) ||
      i.code.toLowerCase().includes(q) ||
      i.name.toLowerCase().includes(q)
    );
    setFiltered(f);
    setCurrentPage(1);
  }, [items, searchQuery]);

  const handleSort = col => {
    let dir = 'asc';
    if (sortCol === col && sortDir === 'asc') dir = 'desc';
    setSortCol(col);
    setSortDir(dir);

    const sorted = [...filtered].sort((a, b) => {
      const A = (a[col] || '').toString().toLowerCase();
      const B = (b[col] || '').toString().toLowerCase();
      return dir === 'asc' ? A.localeCompare(B) : B.localeCompare(A);
    });
    setFiltered(sorted);
  };

  const handleDelete = async () => {
    await deleteData('reportingTypes', delItem.id);
    setShowDelete(false);
  };

  const idxLast = currentPage * rowsPerPage;
  const idxFirst = idxLast - rowsPerPage;
  const pageItems = filtered.slice(idxFirst, idxLast);

  return (
    <>
      <InputGroup className="mb-3">
        <InputGroup.Text><Search /></InputGroup.Text>
        <FormControl
          placeholder="Search Reporting Types"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </InputGroup>

      <Table hover>
        <thead>
          <tr>
            <th>
              <OverlayTrigger
                placement="top"
                overlay={<Tooltip>Add Reporting Type</Tooltip>}
              >
                <Button
                  variant="link"
                  className="icon-hover me-3"
                  onClick={() => setShowForm(true)}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  {isHovered
                    ? <PlusCircleFill size={20} />
                    : <PlusCircle size={20} />}
                </Button>
              </OverlayTrigger>
            </th>
            {['factory', 'code', 'name'].map(col => (
              <th key={col}>
                <div className="d-flex justify-content-between align-items-center">
                  {col.charAt(0).toUpperCase() + col.slice(1)}
                  <Button variant="white" onClick={() => handleSort(col)}>
                    <ArrowDownUp />
                  </Button>
                </div>
              </th>
            ))}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {pageItems.map(item => (
            <tr key={item.id}>
              <td></td>
              <td>{item.factory}</td>
              <td>{item.code}</td>
              <td>{item.name}</td>
              <td>
                <OverlayTrigger overlay={<Tooltip>Edit</Tooltip>}>
                  <Button
                    variant="white"
                    size="sm"
                    onClick={() => { setEditItem(item); setShowForm(true); }}
                  >
                    <Wrench />
                  </Button>
                </OverlayTrigger>
                {' '}
                <OverlayTrigger overlay={<Tooltip>Delete</Tooltip>}>
                  <Button
                    variant="white"
                    size="sm"
                    onClick={() => { setDelItem(item); setShowDelete(true); }}
                  >
                    <Trash color="red" />
                  </Button>
                </OverlayTrigger>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between align-items-center">
        <span>
          Showing {idxFirst + 1}–{Math.min(idxLast, filtered.length)} of {filtered.length}
        </span>
        <Pagination>
          {/* your pagination items here */}
        </Pagination>
      </div>

      <Modal show={showForm} onHide={() => setShowForm(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editItem ? 'Edit' : 'Add'} Reporting Type</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <ReportingTypeForm
            reportingType={editItem}
            onReportingTypeAdded={() => setShowForm(false)}
            onReportingTypeUpdated={() => setShowForm(false)}
            onModalClose={() => setShowForm(false)}
            registerCleanup={fn => (cleanupRef.current = fn)}
          />
        </Modal.Body>
      </Modal>

      <Modal show={showDelete} onHide={() => setShowDelete(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Remove this reporting type?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default ReportingTypeList;
